import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../types';

// ================================
// APP SELECTORS (UNCHANGED)
// ================================
export const selectIsInitialized = (state: RootState) => state.app.isInitialized;
export const selectApiStatus = (state: RootState) => state.app.apiStatus;
export const selectGlobalError = (state: RootState) => state.app.globalError;
export const selectGlobalLoading = (state: RootState) => state.app.isLoading;

// ================================
// FIELDS SELECTORS (UPDATED FOR DYNAMICFIELD)
// ================================
export const selectAllFields = (state: RootState) => state.fields.items; // Now DynamicField[]
export const selectFieldsLoading = (state: RootState) => state.fields.loading;
export const selectFieldsError = (state: RootState) => state.fields.error;
export const selectFieldById = (state: RootState, fieldId: string) =>
  state.fields.items.find(field => field.field_id === fieldId); // Updated property name

// New field selectors
export const selectActiveFields = (state: RootState) =>
  state.fields.items.filter(field => field.is_active);

export const selectFieldsWithInstructions = (state: RootState) =>
  state.fields.items.filter(field => field.prompt_instructions && field.prompt_instructions.length > 0);

// ================================
// CASES SELECTORS (UPDATED FOR DYNAMICCASE)
// ================================
export const selectCasesForField = (state: RootState, fieldId: string) =>
  state.cases.itemsByField[fieldId] || []; // Now DynamicCase[]

export const selectCasesLoadingForField = (state: RootState, fieldId: string) =>
  state.cases.loading[fieldId] || false;

export const selectCasesErrorForField = (state: RootState, fieldId: string) =>
  state.cases.errors[fieldId] || null;

export const selectCaseById = (state: RootState, caseId: string) => {
  for (const cases of Object.values(state.cases.itemsByField)) {
    const foundCase = cases.find(c => c.id === caseId);
    if (foundCase) return foundCase;
  }
  return undefined;
};

// ================================
// AUTH SELECTORS (NEW)
// ================================
export const selectAuthUser = (state: RootState) => state.auth.user;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectAuthIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;

export const selectAllCases = (state: RootState) => {
  return Object.values(state.cases.itemsByField).flat(); // Now DynamicCase[]
};

// New case selectors for processing status and difficulty
export const selectCasesByStatus = (state: RootState, status: string) =>
  Object.values(state.cases.itemsByField)
    .flat()
    .filter(c => c.processing_status === status);

export const selectCasesByDifficulty = (state: RootState, difficulty: string) =>
  Object.values(state.cases.itemsByField)
    .flat()
    .filter(c => c.difficulty_level === difficulty);

export const selectCompletedCases = (state: RootState) =>
  selectCasesByStatus(state, 'completed');

export const selectProcessingCases = (state: RootState) =>
  Object.values(state.cases.itemsByField)
    .flat()
    .filter(c => c.processing_status === 'processing' || c.processing_status === 'pending');

export const selectCasesNeedingReview = (state: RootState) =>
  selectCasesByStatus(state, 'review_required');

// ================================
// PROCESSING SELECTORS (NEW)
// ================================
export const selectActiveProcessing = (state: RootState) => state.processing.activeProcessing;
export const selectBackgroundProcessingStatus = (state: RootState) => state.processing.backgroundProcessingStatus;
export const selectPendingNotification = (state: RootState) => state.processing.pendingNotification;
export const selectRecentlyProcessed = (state: RootState) => state.processing.recentlyProcessed;
export const selectProcessingHistory = (state: RootState) => state.processing.processingHistory;

// Processing status for specific ID
export const selectProcessingStatusById = (state: RootState, processingId: string) =>
  state.processing.activeProcessing[processingId];

// Check if any processing is active
export const selectHasActiveProcessing = (state: RootState) =>
  Object.keys(state.processing.activeProcessing).length > 0;

// Get processing statistics
export const selectProcessingStatistics = createSelector(
  [selectActiveProcessing, selectProcessingHistory],
  (activeProcessing, history) => {
    const activeCount = Object.keys(activeProcessing).length;
    const totalProcessed = history.length;
    const successfulProcessed = history.filter(h => h.success).length;
    const failedProcessed = history.filter(h => !h.success && h.endTime).length;
    
    return {
      activeCount,
      totalProcessed,
      successfulProcessed,
      failedProcessed,
      successRate: totalProcessed > 0 ? (successfulProcessed / totalProcessed) * 100 : 0
    };
  }
);

// ================================
// SESSION SELECTORS (SIMPLIFIED)
// ================================
export const selectCurrentSession = (state: RootState) => state.sessions.current;
export const selectSessionHistory = (state: RootState) => state.sessions.history;

// Updated: Current session with details - now uses DynamicField and DynamicCase
export const selectCurrentSessionWithDetails = createSelector(
  [
    selectCurrentSession,
    selectAllFields,
    (state: RootState) => state.cases.itemsByField
  ],
  (currentSession, fields, casesByField) => {
    if (!currentSession) return null;
    
    // Find the field (updated property name)
    const field = fields.find(f => f.field_id === currentSession.fieldId);
    if (!field) return null;
    
    // Find the case in the field's cases
    const casesForField = casesByField[currentSession.fieldId] || [];
    const dynamicCase = casesForField.find(c => c.id === currentSession.caseId);
    if (!dynamicCase) return null;
    
    return {
      session: currentSession,
      field,
      case: dynamicCase, // Now DynamicCase instead of PatientCase
    };
  }
);

// ================================
// DIAGNOSIS SELECTORS (UPDATED)
// ================================
export const selectDiagnosisCompleteness = (state: RootState) => state.diagnosis.completedElements;
export const selectDiagnosisCompletionRate = (state: RootState) => state.diagnosis.completionRate;
export const selectDiagnosisTotalElements = (state: RootState) => state.diagnosis.totalElements;
export const selectframeworkCategoryStatusList = (state: RootState) => state.diagnosis.frameworkCategoryStatusList;

// Scoring selectors (enhanced)
export const selectScoringRubric = (state: RootState) => state.diagnosis.scoringRubric;
export const selectTotalPointsEarned = (state: RootState) => state.diagnosis.totalPointsEarned;
export const selectTotalPointsPossible = (state: RootState) => state.diagnosis.totalPointsPossible;
export const selectScoringCompletionPercentage = (state: RootState) => state.diagnosis.scoringCompletionPercentage;
export const selectHasScoring = (state: RootState) => state.diagnosis.hasScoring;

// NEW: Enhanced scoring selectors
export const selectScoringRubricWithEnhancements = (state: RootState) => state.diagnosis.scoringRubric;

// NEW: Get categories with messages
export const selectCategoriesWithMessages = createSelector(
  [selectScoringRubric],
  (rubric) => rubric.filter(item => item.messages && item.messages.length > 0)
);

// NEW: Get categories with specific instructions
export const selectCategoriesWithInstructions = createSelector(
  [selectScoringRubric],
  (rubric) => rubric.filter(item => item.categorySpecificInstructions && item.categorySpecificInstructions.length > 0)
);

// NEW: Get total message count across all categories
export const selectTotalMessagesCount = createSelector(
  [selectScoringRubric],
  (rubric) => rubric.reduce((sum, item) => sum + (item.messages?.length || 0), 0)
);

// NEW: Get enhanced rubric analysis
export const selectRubricAnalysis = createSelector(
  [selectScoringRubric],
  (rubric) => rubric.map(item => ({
    category: item.scoringCategory,
    progress: item.totalPoints > 0 ? Math.round((item.pointsScored / item.totalPoints) * 100) : 0,
    hasInstructions: !!(item.categorySpecificInstructions && item.categorySpecificInstructions.length > 0),
    messageCount: item.messages?.length || 0,
    isCompleted: item.pointsScored >= item.totalPoints,
    recentEvidence: item.messages?.slice(-3) || [], // Last 3 messages
    pointsEarned: item.pointsScored,
    pointsPossible: item.totalPoints,
    instructions: item.categorySpecificInstructions || ""
  }))
);

// NEW: Get enhanced scoring statistics
export const selectEnhancedScoringStatistics = createSelector(
  [selectScoringRubric, selectTotalPointsEarned, selectTotalPointsPossible],
  (rubric, totalEarned, totalPossible) => ({
    // Basic stats
    totalPointsEarned: totalEarned,
    totalPointsPossible: totalPossible,
    completionPercentage: totalPossible > 0 ? Math.round((totalEarned / totalPossible) * 100) : 0,
    
    // Enhanced stats
    totalCategories: rubric.length,
    completedCategories: rubric.filter(item => item.pointsScored >= item.totalPoints).length,
    categoriesWithMessages: rubric.filter(item => item.messages && item.messages.length > 0).length,
    categoriesWithInstructions: rubric.filter(item => item.categorySpecificInstructions && item.categorySpecificInstructions.length > 0).length,
    totalMessages: rubric.reduce((sum, item) => sum + (item.messages?.length || 0), 0),
    averageMessagesPerCategory: rubric.length > 0 
      ? Math.round((rubric.reduce((sum, item) => sum + (item.messages?.length || 0), 0) / rubric.length) * 10) / 10
      : 0
  })
);

// UPDATE: Enhanced version of existing selector
export const selectCompletedCriteria = createSelector(
  [selectScoringRubric],
  (rubric) => rubric.filter(item => item.pointsScored >= item.totalPoints).map(item => ({
    category: item.scoringCategory,
    pointsEarned: item.pointsScored,
    totalPoints: item.totalPoints,
    evidence: item.messages || [],
    instructions: item.categorySpecificInstructions || ""
  }))
);

// UPDATE: Enhanced version with evidence tracking
export const selectCriteriaWithNotes = createSelector(
  [selectScoringRubric],
  (rubric) => rubric.filter(item => 
    (item.evaluationNotes && item.evaluationNotes.length > 0) ||
    (item.messages && item.messages.length > 0)
  ).map(item => ({
    category: item.scoringCategory,
    notes: item.evaluationNotes,
    evidence: item.messages || [],
    instructions: item.categorySpecificInstructions || "",
    hasEvidence: (item.messages?.length || 0) > 0
  }))
);

// ================================
// MESSAGES SELECTORS (UNCHANGED)
// ================================
export const selectMessages = (state: RootState) => state.messages.items;
export const selectIsProcessing = (state: RootState) => state.messages.isProcessing;

// ================================
// REAL-TIME SELECTORS (UNCHANGED)
// ================================
export const selectWebSocketState = (state: RootState) => state.websocket;
export const selectSpeechState = (state: RootState) => state.speech;
export const selectAvatarState = (state: RootState) => state.avatar;
export const selectAvatarIsConnected = (state: RootState) => state.avatar.isConnected;

// ================================
// FEATURE AVAILABILITY (UPDATED)
// ================================
export const selectFeatureAvailability = createSelector(
  [selectApiStatus, selectCurrentSession],
  (apiStatus, currentSession) => ({
    canUseAvatar: !!((apiStatus?.anam || apiStatus?.tavus) && currentSession),
    canUseSpeech: !!(apiStatus?.deepgram && currentSession),
    canCreateSession: !!apiStatus?.openai,
    canProcessCases: !!apiStatus?.openai, // New: LLM processing capability
    hasActiveSession: !!currentSession,
  })
);

// ================================
// DIAGNOSTIC READINESS (UPDATED)
// ================================
export const selectDiagnosticReadiness = createSelector(
  [
    selectCurrentSessionWithDetails,
    (state: RootState) => state.websocket.isConnected,
    selectIsProcessing,
    selectFeatureAvailability,
  ],
  (sessionDetails, isConnected, isProcessing, features) => ({
    canAskQuestions: !!(sessionDetails && isConnected && !isProcessing),
    canSubmitDiagnosis: !!(sessionDetails && isConnected),
    canUseSpeech: features.canUseSpeech && isConnected,
    canUseAvatar: features.canUseAvatar && isConnected,
    canProcessCases: features.canProcessCases, // New capability
    sessionActive: !!sessionDetails,
    processingQuestion: isProcessing,
  })
);

// ================================
// PERFORMANCE METRICS (UPDATED)
// ================================
export const selectPerformanceMetrics = createSelector(
  [selectCurrentSession, selectMessages],
  (currentSession, messages) => {
    if (!currentSession) return null;
    
    const medicalProviderMessages = messages.filter(m => m.type === 'medical-provider').length;
    const patientMessages = messages.filter(m => m.type === 'patient').length;
    const systemMessages = messages.filter(m => m.type === 'system').length;
    
    // Calculate session duration from string timestamp
    let sessionDuration = 0;
    if (currentSession.startTime) {
      try {
        const startTime = new Date(currentSession.startTime).getTime();
        sessionDuration = Date.now() - startTime;
      } catch (error) {
        console.warn('Failed to parse session start time:', currentSession.startTime);
        sessionDuration = 0;
      }
    }
    
    return {
      questionsAsked: medicalProviderMessages,
      responsesReceived: patientMessages,
      systemMessages,
      totalMessages: messages.length,
      sessionDuration,
      // REMOVED: categoriesCovered (no longer tracking categories)
    };
  }
);

// ================================
// HELPER SELECTORS (UPDATED)
// ================================

// Get cases with field info (memoized)
export const selectCasesWithFieldInfo = createSelector(
  [
    selectCasesForField,
    selectFieldById,
    selectCasesLoadingForField,
    selectCasesErrorForField,
  ],
  (cases, field, loading, error) => ({
    cases, // Now DynamicCase[]
    field,  // Now DynamicField
    loading,
    error,
    count: cases.length,
  })
);

// ================================
// COMBINED DATA SELECTORS (NEW)
// ================================

// Get comprehensive case data with processing status
// Usage: selectCaseWithProcessingInfo(state, caseId)
export const selectCaseWithProcessingInfo = (state: RootState, caseId: string) => {
  const caseData = selectCaseById(state, caseId);
  if (!caseData) return null;
  
  const activeProcessing = selectActiveProcessing(state);
  const recentlyProcessed = selectRecentlyProcessed(state);
  
  // Check if case is currently being processed
  const isBeingProcessed = Object.values(activeProcessing).some(
    status => status.caseId === caseId
  );
  
  // Check if case was recently processed
  const isRecentlyProcessed = recentlyProcessed.some(c => c.id === caseId);
  
  return {
    ...caseData,
    isBeingProcessed,
    isRecentlyProcessed,
  };
};

// Get field with case statistics
// Usage: selectFieldWithCaseStats(state, fieldId)
export const selectFieldWithCaseStats = (state: RootState, fieldId: string) => {
  const field = selectFieldById(state, fieldId);
  const cases = selectCasesForField(state, fieldId);
  
  if (!field) return null;
  
  const caseStats = {
    total: cases.length,
    completed: cases.filter(c => c.processing_status === 'completed').length,
    processing: cases.filter(c => c.processing_status === 'processing' || c.processing_status === 'pending').length,
    needingReview: cases.filter(c => c.processing_status === 'review_required').length,
    failed: cases.filter(c => c.processing_status === 'failed').length,
    byDifficulty: {
      beginner: cases.filter(c => c.difficulty_level === 'beginner').length,
      intermediate: cases.filter(c => c.difficulty_level === 'intermediate').length,
      advanced: cases.filter(c => c.difficulty_level === 'advanced').length,
    }
  };
  
  return {
    ...field,
    caseStats,
  };
};
